import { Fragment, useState, type CSSProperties } from 'react';
import { Check, AlertCircle, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import {
  NormalStep,
  isCompleteForApplicant,
  needsAttention,
  bucketOf,
  hasAuthoredPhases,
  hasFullJourneyCoverage,
  PHASE_BUCKETS,
} from '../lib/steps';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Snake-path program journey, ported from ParticipantPortalPrototype's
 * ProgramJourney{Card,Timeline,Path,Step,Bookends} (client/src/components/dashboard).
 * Desktop: an S-shaped trail — one horizontal row per phase, alternating
 * direction, joined by U-turn curves — with a dashed track and a solid
 * overlay up to the last consecutively completed step. Mobile: a vertical
 * trail. Generalised to any number of phases (the original hard-coded 3).
 */

interface ProgramJourneyProps {
  steps: NormalStep[];
  /** Application fully complete — paints the whole trail solid + green flag. */
  complete: boolean;
  onStepClick?: (step: NormalStep) => void;
}

interface JourneyRow {
  key: string;
  name: string;
  steps: { step: NormalStep; index: number }[];
}

const ROW_Y0 = 120;
const ROW_GAP = 128;
const MOBILE_X = 200;
const MOBILE_STEP_GAP = 68;
const MOBILE_PHASE_GAP = 50;

/**
 * Group steps into one row per phase, keyed by phaseKey and deduped via a Map
 * (first-appearance order) — exactly how the ApplicationPage stepper/phase tabs
 * group them. Consecutive-only grouping breaks when Globus returns steps whose
 * phases interleave (it produced Phase 1 / 2 / 4 / 1-again). Global step indices
 * are (re)assigned in this grouped order so the journey's numbering + completion
 * state marry the stepper.
 */
function groupPhases(steps: NormalStep[]): JourneyRow[] {
  const map = new Map<string, JourneyRow>();
  const order: string[] = [];
  steps.forEach((step) => {
    if (!map.has(step.phaseKey)) {
      map.set(step.phaseKey, { key: step.phaseKey, name: step.phaseName, steps: [] });
      order.push(step.phaseKey);
    }
    map.get(step.phaseKey)!.steps.push({ step, index: -1 });
  });
  const rows = order.map((k) => map.get(k)!);
  let idx = 0;
  rows.forEach((row) => row.steps.forEach((entry) => { entry.index = idx++; }));
  return rows;
}

/**
 * Bucket band labels, one per row, present only on the FIRST row of each run of
 * rows sharing a portal bucket — so the three portal phases read as bands over
 * the per-journey-position rows.
 *
 * The journey deliberately keeps ONE ROW PER ORIGINAL PHASE rather than
 * collapsing to three rows: `ltrX` spreads a row across a fixed 950px, so a
 * 3-row layout of ~20 steps gives a ~50px node pitch against 60px nodes and
 * label pills up to 150px wide — nodes and labels would overlap and every label
 * would truncate. Banding gets the 3-phase contract across without touching any
 * of the SVG geometry (ltrX / stepXY / snakePath are unchanged).
 *
 * Returns all-undefined unless EVERY step carries a journey position — the same
 * gate groupSteps uses, so the bands and the phase tabs never disagree. With no
 * coverage every step would bucket to "Apply" and a single band spanning the
 * whole journey would assert something untrue; with PARTIAL coverage the bands
 * would be drawn from the unstamped steps' fallback bucket, which is worse
 * (confidently wrong rather than absent).
 */
function bucketBands(rows: JourneyRow[], steps: NormalStep[]): (string | undefined)[] {
  // Authored phase structure (Asana 1217121013432648): each row IS an authored
  // phase and already renders its authored name — overlaying the portal's
  // bucket labels on top would reintroduce the invented names. No bands.
  if (hasAuthoredPhases(steps)) return rows.map(() => undefined);
  if (!hasFullJourneyCoverage(steps)) return rows.map(() => undefined);
  let previous: string | undefined;
  return rows.map((row) => {
    const first = row.steps[0]?.step;
    if (!first) return undefined;
    const id = bucketOf(first);
    if (id === previous) return undefined;
    previous = id;
    return PHASE_BUCKETS.find((b) => b.id === id)?.label;
  });
}

const BAND_FONT_SIZE = 14;
const BAND_LETTER_SPACING = 1.5;

const rowY = (row: number) => ROW_Y0 + row * ROW_GAP;

/** Horizontal position within a left-to-right row (mirrored for odd rows). */
function ltrX(count: number, pos: number): number {
  if (count === 1) return 625;
  if (count === 2) return 350 + pos * 400;
  if (count === 3) return 225 + pos * 325;
  return 150 + pos * (950 / (count - 1));
}

function stepXY(row: number, count: number, pos: number): { x: number; y: number } {
  const x = ltrX(count, pos);
  return { x: row % 2 === 0 ? x : 1250 - x, y: rowY(row) };
}

/** Snake path from the start marker through `endRow`, stopping at `endX`. */
function snakePath(endRow: number, endX: number): string {
  let d = `M -20 ${rowY(0)}`;
  for (let r = 0; r < endRow; r++) {
    const y = rowY(r);
    const y2 = rowY(r + 1);
    const mid = (y + y2) / 2;
    d += r % 2 === 0
      ? ` L 1200 ${y} Q 1250 ${y} 1250 ${mid} Q 1250 ${y2} 1200 ${y2}`
      : ` L 50 ${y} Q 0 ${y} 0 ${mid} Q 0 ${y2} 50 ${y2}`;
  }
  return d + ` L ${endX} ${rowY(endRow)}`;
}

/**
 * Word-boundary truncation + width estimation for the desktop label pills,
 * ported verbatim from the old ProgramJourneyStep.
 */
function truncateAtWordBoundary(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const budget = maxChars - 1;
  const slice = text.substring(0, budget);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace >= Math.floor(budget / 2)) return slice.substring(0, lastSpace).trim() + '…';
  return slice.trim() + '…';
}

function estimateTextWidth(text: string, fontSize: number): number {
  let width = 0;
  const scale = fontSize / 13;
  for (const char of text) {
    if (/[iIl1!|']/.test(char)) width += 4 * scale;
    else if (/[wmWM@]/.test(char)) width += 10 * scale;
    else if (/[A-Z0-9]/.test(char)) width += 8 * scale;
    else if (/[a-z]/.test(char)) width += 6.5 * scale;
    else if (char === ' ') width += 4 * scale;
    else width += 6 * scale;
  }
  return width;
}

function phaseColor(row: JourneyRow, currentIndex: number, complete: boolean): string {
  if (complete) return 'var(--ok)';
  const first = row.steps[0].index;
  const last = row.steps[row.steps.length - 1].index;
  if (currentIndex > last) return 'var(--ok)';
  if (currentIndex < first) return 'var(--ink-2)';
  return 'var(--brand)';
}

interface NodeProps {
  step: NormalStep;
  index: number;
  x: number;
  y: number;
  isMobile: boolean;
  isCurrent: boolean;
  maxChars: number;
  maxPillWidth: number;
  fontSize: number;
  onStepClick?: (step: NormalStep) => void;
}

function JourneyNode({ step, index, x, y, isMobile, isCurrent, maxChars, maxPillWidth, fontSize, onStepClick }: NodeProps) {
  const attention = needsAttention(step);
  const done = isCompleteForApplicant(step);
  const clickable = done || isCurrent || attention;

  const circleRadius = isMobile ? 32 : 30;
  const checkSize = isMobile ? 24 : 24;
  const checkOffset = isMobile ? -12 : -12;
  const pulseValues = isMobile ? '32;37;32' : '30;38;30';
  const pinScale = isMobile ? 1.0 : 1.0;
  const numberFontSize = isMobile ? 20 : 20;

  const displayName = truncateAtWordBoundary(step.title, maxChars);
  const pillWidth = Math.min(estimateTextWidth(displayName, fontSize) + 20, maxPillWidth);
  const labelColor = attention ? 'var(--err)' : done ? 'var(--ok)' : isCurrent ? 'var(--brand)' : 'var(--ink)';

  return (
    <g
      transform={`translate(${x},${y})`}
      role="button"
      tabIndex={clickable ? 0 : -1}
      aria-label={step.title}
      aria-disabled={clickable ? undefined : 'true'}
      onClick={() => clickable && onStepClick?.(step)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && clickable) {
          e.preventDefault();
          onStepClick?.(step);
        }
      }}
      style={{
        pointerEvents: clickable ? 'all' : 'none',
        cursor: clickable ? 'pointer' : 'not-allowed',
      }}
    >
      {attention ? (
        <g>
          <circle r={circleRadius} fill="var(--err)" />
          <AlertCircle size={checkSize} strokeWidth={3} color="white" x={checkOffset} y={checkOffset} />
        </g>
      ) : done ? (
        <g>
          <circle r={circleRadius} fill="var(--ok)" />
          <Check size={checkSize} strokeWidth={3} color="white" x={checkOffset} y={checkOffset} />
        </g>
      ) : isCurrent ? (
        <g>
          <circle r={circleRadius + 4} fill="none" stroke="var(--brand)" strokeWidth="2" opacity="0.7">
            <animate attributeName="r" values={pulseValues} dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.7;0.3;0.7" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <circle r={circleRadius} fill="var(--card)" stroke="var(--brand)" strokeWidth="3" filter="url(#journeyCurrentGlow)" />
          <g transform={`scale(${pinScale})`}>
            <path
              d="M 0 -8 C -6 -8, -8 -2, -8 2 C -8 6, -4 10, 0 16 C 4 10, 8 6, 8 2 C 8 -2, 6 -8, 0 -8 Z"
              fill="var(--brand)"
              stroke="var(--brand-600)"
              strokeWidth="1"
            />
            <circle cx="0" cy="0" r="3" fill="white" />
          </g>
        </g>
      ) : (
        <g>
          <circle r={circleRadius} fill="var(--card)" stroke="var(--line)" strokeWidth="2" />
          <text y="6" fontSize={numberFontSize} textAnchor="middle" fill="var(--ink-2)" fontWeight="600">
            {index + 1}
          </text>
        </g>
      )}

      {isMobile ? (
        <g transform="translate(55, 0)">
          <title>{step.title}</title>
          <text y="7" fontSize="16" textAnchor="start" fill={labelColor} fontWeight="500">
            {displayName}
          </text>
        </g>
      ) : (
        <g transform="translate(0, 52)">
          <title>{step.title}</title>
          <rect
            x={-pillWidth / 2}
            y={-16}
            width={pillWidth}
            height="32"
            rx="16"
            fill={done ? 'var(--ok-soft-bg)' : attention ? 'var(--err-soft-bg)' : isCurrent ? 'var(--brand-50)' : 'var(--bg)'}
            stroke={done ? 'var(--ok)' : attention ? 'var(--err)' : isCurrent ? 'var(--brand)' : 'var(--line)'}
            strokeWidth="1"
          />
          <text y={fontSize > 11 ? 5 : 4} fontSize={fontSize} textAnchor="middle" fill={labelColor} fontWeight="500">
            {displayName}
          </text>
        </g>
      )}
    </g>
  );
}

function Bookends({ isMobile, complete, endX, endY }: { isMobile: boolean; complete: boolean; endX: number; endY: number }) {
  const flagColor = complete ? 'var(--ok)' : 'var(--brand)';
  const pencil = (
    <g stroke="var(--brand)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </g>
  );
  const flag = (
    <g stroke={flagColor} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22V4" />
    </g>
  );

  if (isMobile) {
    return (
      <>
        <g transform={`translate(${MOBILE_X}, 20)`}>
          <g transform="scale(2.5)">{pencil}</g>
          <text x="0" y="-40" fontSize="18" fontWeight="600" textAnchor="middle" fill="var(--brand)">
            Start Application
          </text>
        </g>
        <g transform={`translate(${MOBILE_X}, ${endY})`}>
          {complete && (
            <circle cx="0" cy="15" r="50" fill="none" stroke="var(--ok)" strokeWidth="2" opacity="0.3">
              <animate attributeName="r" values="40;55;40" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
            </circle>
          )}
          <g transform="scale(2.5)">{flag}</g>
          <text x="0" y="70" fontSize="18" fontWeight="600" textAnchor="middle" fill={flagColor}>
            On Program
          </text>
        </g>
      </>
    );
  }

  return (
    <>
      <g transform="translate(-180, 10)">
        <g transform="scale(2.5)">{pencil}</g>
        <text x="30" y="75" fontSize="18" fontWeight="600" textAnchor="middle" fill="var(--brand)">
          Start Application
        </text>
      </g>
      <g transform={`translate(${endX}, ${endY})`}>
        {complete && (
          <circle cx="20" cy="30" r="50" fill="none" stroke="var(--ok)" strokeWidth="2" opacity="0.3">
            <animate attributeName="r" values="40;55;40" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
          </circle>
        )}
        <g transform="scale(2.5)">{flag}</g>
        <text x="20" y="75" fontSize="18" fontWeight="600" textAnchor="middle" fill={flagColor}>
          On Program
        </text>
      </g>
    </>
  );
}

export default function ProgramJourney({ steps: rawSteps, complete, onStepClick }: ProgramJourneyProps) {
  const isMobile = useIsMobile();
  if (rawSteps.length === 0) return null;

  const rows = groupPhases(rawSteps);
  // Single source of truth: steps in phase-grouped order. Every index, position
  // and completion check below runs on THIS list so the journey stays in lockstep
  // with the phase grouping (and therefore the stepper).
  const steps = rows.flatMap((row) => row.steps.map((s) => s.step));
  const journeyComplete = complete && steps.every(isCompleteForApplicant);
  const currentIndex = journeyComplete ? steps.length : steps.findIndex((s) => !isCompleteForApplicant(s));

  // Solid overlay covers the consecutively-completed prefix (like the original).
  let lastCompleted = -1;
  for (const s of steps) {
    if (!isCompleteForApplicant(s)) break;
    lastCompleted++;
  }

  if (isMobile) {
    return (
      <MobileJourney
        rows={rows}
        steps={steps}
        currentIndex={currentIndex}
        complete={journeyComplete}
        onStepClick={onStepClick}
      />
    );
  }

  // Desktop: one snaking row per phase, banded by portal bucket.
  const bands = bucketBands(rows, steps);
  const lastRow = rows.length - 1;
  const lastRowLtr = lastRow % 2 === 0;
  const positions = rows.flatMap((row, r) =>
    row.steps.map((_, pos) => ({ row: r, ...stepXY(r, row.steps.length, pos) })),
  );
  const height = rowY(lastRow) + 110;

  const track = snakePath(lastRow, lastRowLtr ? 1200 : 50);
  let solid = '';
  if (journeyComplete) {
    solid = snakePath(lastRow, lastRowLtr ? 1250 : 0);
  } else if (lastCompleted >= 0) {
    const p = positions[lastCompleted];
    solid = snakePath(p.row, p.x);
  }

  // Fit the viewBox to the actual drawing so there's no dead margin on the left
  // (the old fixed -350 reserved room for the flag, which only lands on the left
  // for odd-numbered last rows). Trimming the empty margin also lets the whole
  // trail scale up to fill the card, making the steps larger and easier to read.
  // Bounds cover the widest FIXED elements per side: the "Start Application"
  // bookend text reaches ~-225; the "On Program" flag reaches ~-310 when it lands
  // on the left (odd last row) or ~1320 when on the right (even last row); nodes,
  // pills and the snake U-turns stay within ~1255 on the right. Padded ~25.
  const vbLeft = lastRowLtr ? -250 : -335;
  const vbRight = lastRowLtr ? 1345 : 1300;
  const vbWidth = vbRight - vbLeft;

  return (
    <div className="journey-snake">
      <svg
        viewBox={`${vbLeft} -10 ${vbWidth} ${height + 20}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', minHeight: 160, display: 'block', margin: '0 auto' }}
      >
        <defs>
          <filter id="journeyCurrentGlow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {rows.map((row, r) => (
          <text key={row.key} x="-20" y={rowY(r) - 28} fontSize="26" fontWeight="700" fill={phaseColor(row, currentIndex, journeyComplete)} textAnchor="start">
            {row.name}
          </text>
        ))}

        <path d={track} fill="none" stroke="var(--brand)" strokeWidth="6" strokeDasharray="8,6" opacity="0.3" strokeLinejoin="round" />
        {solid && <path d={solid} fill="none" stroke="var(--brand)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />}

        {rows.map((row, r) => {
          const count = row.steps.length;
          const fontSize = count > 5 ? 13 : 15;
          const maxChars = count > 6 ? 18 : count > 5 ? 22 : 28;
          const maxPillWidth = count > 6 ? 150 : count > 5 ? 175 : 220;
          return row.steps.map(({ step, index }, pos) => {
            const { x, y } = stepXY(r, count, pos);
            return (
              <JourneyNode
                key={step.stepKey}
                step={step}
                index={index}
                x={x}
                y={y}
                isMobile={false}
                isCurrent={index === currentIndex}
                maxChars={maxChars}
                maxPillWidth={maxPillWidth}
                fontSize={fontSize}
                onStepClick={onStepClick}
              />
            );
          });
        })}

        <Bookends isMobile={false} complete={journeyComplete} endX={lastRowLtr ? 1250 : -280} endY={rowY(lastRow) + 30} />

        {/* Bucket bands — the 3 portal phases labelled over the per-phase rows.
            Drawn LAST, over an opaque plate: a band header sits in the gutter
            above its first row, which can graze the preceding row's leftmost
            label pill, and the band is the more important of the two. */}
        {bands.map((label, r) =>
          label ? (
            <g key={`band-${r}`}>
              <rect
                x={-26}
                y={rowY(r) - 74}
                width={estimateTextWidth(label.toUpperCase(), BAND_FONT_SIZE) + BAND_LETTER_SPACING * label.length + 16}
                height={24}
                rx={6}
                fill="var(--bg)"
              />
              <text
                x={-20}
                y={rowY(r) - 57}
                fontSize={BAND_FONT_SIZE}
                fontWeight="700"
                letterSpacing={BAND_LETTER_SPACING}
                fill="var(--ink-2)"
                textAnchor="start"
              >
                {label.toUpperCase()}
              </text>
            </g>
          ) : null,
        )}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Mobile: "Focus Next" layout — an Up-Next hero card with overall
 * progress, then an accordion of phases (each with its own progress
 * bar). Desktop keeps the snake trail above; this branch is mobile-only.
 * ------------------------------------------------------------------ */

const heroCard: CSSProperties = {
  position: 'relative', overflow: 'hidden', backgroundColor: 'var(--card)',
  border: '1px solid var(--brand-50)', borderRadius: 'var(--radius)', padding: 18,
  boxShadow: 'var(--shadow)',
};
const heroDecor: CSSProperties = {
  position: 'absolute', right: -48, top: -48, width: 160, height: 160, borderRadius: '50%',
  backgroundColor: 'var(--brand-50)', opacity: 0.35, pointerEvents: 'none',
};
const heroBadge: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
  borderRadius: '50%', backgroundColor: 'var(--brand-50)', color: 'var(--brand)', flexShrink: 0,
};
const heroCta: CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: 12, fontWeight: 600, fontSize: 15,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  color: '#fff', backgroundColor: 'var(--brand)', border: 'none', cursor: 'pointer',
};
const phaseCard: CSSProperties = {
  overflow: 'hidden', backgroundColor: 'var(--card)', border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
};
const phaseBtn: CSSProperties = {
  width: '100%', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  background: 'transparent', border: 'none', cursor: 'pointer',
};

function barTrack(h: number): CSSProperties {
  return { height: h, width: '100%', borderRadius: 999, overflow: 'hidden', backgroundColor: 'var(--line)' };
}

function phaseIconStyle(doneP: boolean, activeP: boolean): CSSProperties {
  return {
    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0,
    backgroundColor: doneP ? 'var(--ok)' : activeP ? 'var(--brand-50)' : 'var(--bg)',
    color: doneP ? '#fff' : activeP ? 'var(--brand)' : 'var(--ink-2)',
    border: doneP ? 'none' : `1px solid ${activeP ? 'var(--brand-50)' : 'var(--line)'}`,
  };
}

type StepState = 'done' | 'current' | 'attention' | 'upcoming';

function StepDot({ state, index }: { state: StepState; index: number }) {
  const base: CSSProperties = {
    width: 28, height: 28, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
  if (state === 'done') return <span style={{ ...base, backgroundColor: 'var(--ok)', color: '#fff' }}><Check size={14} strokeWidth={3} /></span>;
  if (state === 'attention') return <span style={{ ...base, backgroundColor: 'var(--err)', color: '#fff' }}><AlertCircle size={14} strokeWidth={2.5} /></span>;
  if (state === 'current') {
    return (
      <span style={{ ...base, position: 'relative' }}>
        <span className="mj-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: 'var(--brand)', opacity: 0.25 }} />
        <span style={{ ...base, position: 'relative', border: '2px solid var(--brand)', backgroundColor: 'var(--card)', color: 'var(--brand)' }}><MapPin size={12} strokeWidth={2.5} /></span>
      </span>
    );
  }
  return <span style={{ ...base, border: '1px solid var(--line)', backgroundColor: 'var(--card)', color: 'var(--ink-2)', fontSize: 11, fontWeight: 700 }}>{index + 1}</span>;
}

function MobileJourney({ rows, steps, currentIndex, complete, onStepClick }: {
  rows: JourneyRow[];
  steps: NormalStep[];
  currentIndex: number;
  complete: boolean;
  onStepClick?: (step: NormalStep) => void;
}) {
  const bands = bucketBands(rows, steps);
  const done = steps.filter(isCompleteForApplicant).length;
  const total = steps.length || 1;
  const pct = complete ? 100 : Math.round((done / total) * 100);
  const currentStep = currentIndex >= 0 && currentIndex < steps.length ? steps[currentIndex] : undefined;

  const activeKey = rows.find((row) => {
    const first = row.steps[0].index;
    const last = row.steps[row.steps.length - 1].index;
    return currentIndex >= first && currentIndex <= last;
  })?.key ?? rows[0]?.key;

  const [expanded, setExpanded] = useState<string[]>(activeKey ? [activeKey] : []);
  const toggle = (key: string) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  return (
    <div className="journey-focus">
      <div style={heroCard}>
        <div style={heroDecor} />
        {currentStep ? (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={heroBadge}><MapPin size={12} strokeWidth={3} /></span>
              <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: needsAttention(currentStep) ? 'var(--err)' : 'var(--brand)' }}>
                {needsAttention(currentStep) ? 'Action needed' : 'Up next'}
              </span>
            </div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 4px' }}>{currentStep.title}</h3>
            {currentStep.description && (
              <p style={{ fontSize: 14, lineHeight: 1.5, margin: '0 0 16px', color: 'var(--ink-2)' }}>{currentStep.description}</p>
            )}
            <button type="button" className="mj-cta" style={heroCta} onClick={() => onStepClick?.(currentStep)}>
              Continue application <ChevronRight size={18} />
            </button>
          </div>
        ) : (
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ ...heroBadge, width: 32, height: 32, backgroundColor: 'var(--ok)', color: '#fff' }}><Check size={18} strokeWidth={3} /></span>
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 2px' }}>You're all set</h3>
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-2)' }}>Every step is complete — you're on program.</p>
            </div>
          </div>
        )}

        <div style={{ position: 'relative', zIndex: 1, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>Overall progress</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: complete ? 'var(--ok)' : 'var(--brand)' }}>{pct}%</span>
          </div>
          <div style={barTrack(8)}>
            <div style={{ height: '100%', borderRadius: 999, width: `${pct}%`, backgroundColor: complete ? 'var(--ok)' : 'var(--brand)', transition: 'width .8s ease' }} />
          </div>
          <div style={{ fontSize: 11, marginTop: 6, textAlign: 'right', fontWeight: 500, color: 'var(--ink-2)' }}>
            {done} of {total} steps completed
          </div>
        </div>
      </div>

      <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '20px 0 12px' }}>All phases</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((row, r) => {
          const band = bands[r];
          const first = row.steps[0].index;
          const last = row.steps[row.steps.length - 1].index;
          const doneCount = row.steps.filter((s) => isCompleteForApplicant(s.step)).length;
          const phaseDone = doneCount === row.steps.length;
          const phaseActive = !complete && currentIndex >= first && currentIndex <= last;
          const open = expanded.includes(row.key);
          const phasePct = Math.round((doneCount / (row.steps.length || 1)) * 100);
          return (
            <Fragment key={row.key}>
            {/* Bucket band header — the mobile equivalent of the desktop bands:
                the per-phase cards stay, grouped under their portal phase. */}
            {band && (
              <h4 style={{ margin: r === 0 ? '0 0 -4px' : '8px 0 -4px', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
                {band}
              </h4>
            )}
            <div style={phaseCard}>
              <button type="button" className="mj-phase-btn" style={phaseBtn} onClick={() => toggle(row.key)} aria-expanded={open}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={phaseIconStyle(phaseDone, phaseActive)}>
                    {phaseDone ? <Check size={16} strokeWidth={3} /> : <span style={{ fontWeight: 700, fontSize: 13 }}>{r + 1}</span>}
                  </span>
                  <span style={{ textAlign: 'left' }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>{row.name}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)' }}>{doneCount} of {row.steps.length} complete</span>
                  </span>
                </span>
                <span style={{ color: 'var(--ink-2)', display: 'flex' }}>{open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}</span>
              </button>

              <div style={{ padding: '0 16px 12px' }}>
                <div style={barTrack(6)}>
                  <div style={{ height: '100%', borderRadius: 999, width: `${phasePct}%`, backgroundColor: phaseActive ? 'var(--brand)' : 'var(--ok)', transition: 'width .8s ease' }} />
                </div>
              </div>

              {open && (
                <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {row.steps.map(({ step, index }) => {
                    const attention = needsAttention(step);
                    const stepDone = isCompleteForApplicant(step);
                    const isCurrent = !complete && index === currentIndex;
                    const state: StepState = attention ? 'attention' : stepDone ? 'done' : isCurrent ? 'current' : 'upcoming';
                    const clickable = stepDone || isCurrent || attention;
                    return (
                      <div
                        key={step.stepKey}
                        className={`mj-step${clickable ? ' clickable' : ''}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, opacity: state === 'upcoming' ? 0.65 : 1 }}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={step.title}
                        onClick={() => clickable && onStepClick?.(step)}
                        onKeyDown={(e) => {
                          if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            onStepClick?.(step);
                          }
                        }}
                      >
                        <StepDot state={state} index={index} />
                        <span style={{ fontSize: 14, flex: 1, fontWeight: isCurrent ? 700 : 500, color: state === 'upcoming' ? 'var(--ink-2)' : 'var(--ink)' }}>{step.title}</span>
                        {clickable && <ChevronRight size={16} style={{ color: 'var(--line)', flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
